import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { moderationApi } from '../../services/api'
import {
  Alert,
  Button,
  ChangeSet,
  Checkbox,
  DataTable,
  DataView,
  Dialog,
  DropdownMenu,
  Field,
  Fieldset,
  Input,
  PageHeader,
  PageLayout,
  Radio,
  StatusBadge,
} from '../../components/common'
import { formatAge } from '../../components/moderation/moderationReviewModel'
import {
  FLAGGED_CONTENT_DESCRIPTION,
  FLAGGED_CONTENT_OPTIONS,
  OLD_APPROVAL_HINT,
  RECHECK_LIVE_DESCRIPTION,
  approvalDateError,
  approvalDateIsOld,
  approvalValid,
  enableSuccessMessage,
  flaggedContentChange,
  flaggedContentLabel,
  flaggedContentSuccessMessage,
  labelKindWords,
  labelRuleWords,
  localToday,
  platformSummary,
  recheckLiveLabel,
  reviewQueueSummary,
  switchChange,
  switchPayload,
} from '../../components/moderation/organizationModerationModel'
import styles from './CommandCenterModerationPage.module.css'

const QUERY_KEY = ['command-center', 'moderation-organizations']
const LABEL_MATCHES_QUERY_KEY = ['command-center', 'moderation-label-matches']
// Every brand's GET /:org/moderation/status (useModerationStatus).
const MODERATION_STATUS_QUERY_ROOT = ['moderation-status']

const LABEL_MATCHES_DESCRIPTION = 'Tags, creator names, call-to-action links and caption labels are public. New and edited ones are checked against phrase rules when saved; this check finds the ones saved earlier that match a rule now.'

const DISABLE_DESCRIPTION = 'Disabling stops new checks. Posts already held stay held: a brand admin decides review holds in the moderation queue, which keeps working, and Wager releases posts still waiting for a check.'

// An organization without a stored choice reviews flagged posts by hand.
function currentHumanReview(organization) {
  return organization?.human_review_required !== false
}

// CommandCenterModerationPage turns content moderation on or off per
// organization and sets what happens to flagged posts. Releasing a held post
// is not done here: it is an override on the post itself.
export default function CommandCenterModerationPage() {
  const queryClient = useQueryClient()
  // pending is { organization, action }, action one of 'enable', 'disable'
  // or 'flagged_content'.
  const [pending, setPending] = useState(null)
  const [approval, setApproval] = useState({ date: '', reference: '' })
  const [humanReview, setHumanReview] = useState(true)
  const [recheckLive, setRecheckLive] = useState(false)
  const [actionError, setActionError] = useState(null)

  const organizationsQuery = useQuery({ queryKey: QUERY_KEY, queryFn: () => moderationApi.listOrganizations() })
  const organizations = organizationsQuery.data?.organizations || []
  // The label scan reads every brand's labels, so it runs only when asked.
  const labelMatchesQuery = useQuery({
    queryKey: LABEL_MATCHES_QUERY_KEY,
    queryFn: () => moderationApi.listLabelMatches(),
    enabled: false,
  })
  const labelMatches = Array.isArray(labelMatchesQuery.data?.matches) ? labelMatchesQuery.data.matches : []
  const labelsTruncated = labelMatchesQuery.data?.truncated === true
  const labelsChecked = labelMatchesQuery.isFetched

  const switchMutation = useMutation({
    mutationFn: ({ organization, action, approval: submittedApproval, humanReview: submittedHumanReview, recheckLive: submittedRecheck }) => (
      action === 'flagged_content'
        ? moderationApi.setOrganizationFlaggedContent(organization.organization_id, submittedHumanReview)
        : moderationApi.setOrganizationModeration(
          organization.organization_id,
          switchPayload(
            action === 'enable',
            submittedApproval,
            { value: submittedHumanReview, current: currentHumanReview(organization) },
            { recheckLivePosts: submittedRecheck },
          ),
        )
    ),
    onMutate: () => setActionError(null),
    onSuccess: (data, { organization, action, humanReview: submitted, recheckLive: submittedRecheck }) => {
      if (action === 'flagged_content') {
        toast.success(flaggedContentSuccessMessage(organization, submitted, data?.reviews_flipped))
      } else if (action === 'enable') {
        toast.success(enableSuccessMessage(organization, submittedRecheck, data))
      } else {
        toast.success(`Content moderation disabled for ${organization.name}`)
      }
      setPending(null)
      setRecheckLive(false)
      setApproval({ date: '', reference: '' })
      queryClient.invalidateQueries({ queryKey: QUERY_KEY })
      // Brand pages open in this tab show the new state on their next read.
      queryClient.invalidateQueries({ queryKey: MODERATION_STATUS_QUERY_ROOT })
    },
    onError: (error) => {
      const message = error.response?.data?.error || error.message || 'The change could not be saved.'
      setActionError(message)
      toast.error(message)
    },
  })

  const close = () => {
    if (switchMutation.isPending) return
    setPending(null)
    setActionError(null)
    setApproval({ date: '', reference: '' })
    setRecheckLive(false)
  }
  const openAction = (organization, action) => {
    setActionError(null)
    if (action === 'enable') {
      setApproval({
        date: organization.approval_date || '',
        reference: organization.approval_reference || '',
      })
      setRecheckLive(false)
    }
    setHumanReview(currentHumanReview(organization))
    setPending({ organization, action })
  }
  const columns = [
    { key: 'name', label: 'Organization', rowHeader: true, priority: 'primary', sticky: 'start' },
    {
      key: 'enabled',
      label: 'Moderation',
      priority: 'primary',
      // At phone width the count columns are hidden, so the cell also
      // carries the queue line; what a flag does shows for enabled brands at
      // every width.
      render: (organization) => {
        const queue = reviewQueueSummary(organization, formatAge)
        return (
          <span className={styles.moderationCell}>
            <StatusBadge status={organization.enabled ? 'success' : 'default'}>{organization.enabled ? 'On' : 'Off'}</StatusBadge>
            {organization.enabled ? <span className={styles.cellNote}>{flaggedContentLabel(currentHumanReview(organization))}</span> : null}
            {queue ? <span className={styles.phoneOnly}>{queue}</span> : null}
          </span>
        )
      },
    },
    {
      key: 'approval_date',
      label: 'Approval',
      priority: 'secondary',
      render: (organization) => (organization.approval_date
        ? `${organization.approval_date} · ${organization.approval_reference || ''}`
        : 'Not recorded'),
    },
    { key: 'open_reviews', label: 'Open reviews', numeric: true, priority: 'secondary' },
    {
      key: 'auto_rejected_reviews',
      label: 'Auto-rejected',
      numeric: true,
      priority: 'secondary',
      render: (organization) => organization.auto_rejected_reviews ?? 0,
    },
    {
      key: 'oldest_open_review_age_seconds',
      label: 'Oldest',
      numeric: true,
      priority: 'tertiary',
      render: (organization) => formatAge(organization.oldest_open_review_age_seconds),
    },
    {
      key: 'actions',
      label: <span className="wf-visually-hidden">Actions</span>,
      align: 'right',
      priority: 'primary',
      sticky: 'end',
      render: (organization) => (organization.enabled ? (
        <DropdownMenu
          triggerLabel={`Actions for ${organization.name}`}
          items={[
            { key: 'flagged_content', label: 'Change flagged content', onSelect: () => openAction(organization, 'flagged_content') },
            { key: 'disable', label: 'Disable moderation', danger: true, onSelect: () => openAction(organization, 'disable') },
          ]}
        />
      ) : (
        <Button size="compact" variant="primary" onClick={() => openAction(organization, 'enable')}>Enable</Button>
      )),
    },
  ]
  const action = pending?.action
  const name = pending?.organization.name
  const today = localToday()
  const unchangedChoice = action === 'flagged_content' && humanReview === currentHumanReview(pending.organization)
  const canSubmit = Boolean(pending) && (action !== 'enable' || approvalValid(approval, today)) && !unchangedChoice
  const flaggedContentFieldset = (help) => (
    <Fieldset label="Flagged content" help={help}>
      {FLAGGED_CONTENT_OPTIONS.map((option) => (
        <Radio
          key={option.label}
          name="flagged-content"
          value={option.value ? 'human_review' : 'auto_reject'}
          checked={humanReview === option.value}
          onChange={() => setHumanReview(option.value)}
          disabled={switchMutation.isPending}
          label={option.label}
          description={option.description}
        />
      ))}
    </Fieldset>
  )
  const dialog = {
    enable: {
      title: `Enable content moderation for ${name}`,
      description: 'Record the organization\'s data-processing approval first. No content reaches the moderation provider without it.',
      confirm: 'Enable moderation',
      variant: 'primary',
    },
    disable: {
      title: `Disable content moderation for ${name}`,
      description: DISABLE_DESCRIPTION,
      confirm: 'Disable moderation',
      variant: 'danger',
    },
    flagged_content: {
      title: `Change flagged content for ${name}`,
      description: FLAGGED_CONTENT_DESCRIPTION,
      confirm: 'Save flagged content',
      variant: 'primary',
    },
  }[action] || { title: '', description: '', confirm: '', variant: 'primary' }

  return (
    <PageLayout size="wide">
      <PageHeader
        title="Content moderation"
        context="Command Center / Content moderation"
        subtitle="Turn moderation on per organization once its data-processing approval is recorded, and watch each organization's review queue."
      />
      {organizationsQuery.data?.platform ? (
        <Alert variant={organizationsQuery.data.platform.enabled ? 'info' : 'warning'}>
          {platformSummary(organizationsQuery.data.platform)}
        </Alert>
      ) : null}

      <DataView
        items={organizations}
        loading={organizationsQuery.isLoading}
        error={organizationsQuery.error}
        emptyTitle="No organizations"
        onRetry={() => organizationsQuery.refetch()}
      >
        <DataTable
          label="Organization content moderation"
          tableLabel="Organization content moderation"
          data={organizations}
          columns={columns}
          keyField="organization_id"
          density="compact"
          responsiveColumns
        />
      </DataView>

      <section className={styles.section} aria-labelledby="label-matches-title">
        <div className={styles.sectionHeader}>
          <div>
            <h2 id="label-matches-title" className={styles.sectionTitle}>Labels that match phrase rules</h2>
            <p className={styles.sectionDescription}>{LABEL_MATCHES_DESCRIPTION}</p>
          </div>
          <Button variant="secondary" loading={labelMatchesQuery.isFetching} onClick={() => labelMatchesQuery.refetch()}>
            {labelsChecked ? 'Check again' : 'Check labels'}
          </Button>
        </div>
        {labelsChecked || labelMatchesQuery.isFetching ? (
          <DataView
            items={labelMatches}
            loading={labelMatchesQuery.isFetching && !labelsChecked}
            error={labelMatchesQuery.error}
            emptyTitle="No matches"
            emptyDescription="No tag, creator name, link or caption label matches a phrase rule."
            onRetry={() => labelMatchesQuery.refetch()}
          >
            <DataTable
              label="Labels that match phrase rules"
              tableLabel="Labels that match phrase rules"
              data={labelMatches.map((match) => ({ ...match, key: `${match.organization?.id}:${match.kind}:${match.id}:${match.label}` }))}
              keyField="key"
              density="compact"
              responsiveColumns
              columns={[
                { key: 'label', label: 'Label', rowHeader: true, priority: 'primary' },
                { key: 'organization', label: 'Organization', priority: 'primary', render: (match) => match.organization?.name || match.organization?.slug || '—' },
                { key: 'kind', label: 'Kind', priority: 'secondary', render: (match) => labelKindWords(match.kind) },
                { key: 'rule', label: 'Matches', priority: 'primary', render: (match) => labelRuleWords(match.rule) },
              ]}
            />
          </DataView>
        ) : null}
        {labelsTruncated ? <p className={styles.sectionDescription}>Showing the first 500 matches. Fix these and check again to see the rest.</p> : null}
      </section>

      <Dialog
        open={Boolean(pending)}
        title={dialog.title}
        description={dialog.description}
        dismissable={!switchMutation.isPending}
        onClose={close}
        footer={(
          <>
            <Button variant="secondary" disabled={switchMutation.isPending} onClick={close}>Cancel</Button>
            <Button
              variant={dialog.variant}
              loading={switchMutation.isPending}
              disabled={!canSubmit}
              onClick={() => pending && switchMutation.mutate({ ...pending, approval, humanReview, recheckLive })}
            >
              {dialog.confirm}
            </Button>
          </>
        )}
      >
        {action === 'enable' ? (
          <>
            <ChangeSet title="Change" items={switchChange(pending.organization, true, approval, humanReview, { recheckLive, today })} />
            <Field label="Approval date" help="The date the organization approved data processing." error={approvalDateError(approval.date, today)}>
              <Input type="date" max={today} value={approval.date} onChange={(event) => setApproval((current) => ({ ...current, date: event.target.value }))} />
            </Field>
            {approvalDateIsOld(approval.date, today) ? <Alert variant="warning">{OLD_APPROVAL_HINT}</Alert> : null}
            {(pending.organization.live_unchecked_posts || 0) > 0 ? (
              <Fieldset label="Posts already live">
                <Checkbox
                  checked={recheckLive}
                  onChange={(event) => setRecheckLive(event.target.checked)}
                  disabled={switchMutation.isPending}
                  label={recheckLiveLabel(pending.organization.live_unchecked_posts)}
                  description={RECHECK_LIVE_DESCRIPTION}
                />
              </Fieldset>
            ) : null}
            <Field label="Approval reference" help="The contract, ticket or document that records the approval.">
              <Input value={approval.reference} onChange={(event) => setApproval((current) => ({ ...current, reference: event.target.value }))} />
            </Field>
            {flaggedContentFieldset('What happens to a post moderation flags for this organization.')}
          </>
        ) : null}
        {action === 'disable' ? <ChangeSet title="Change" items={switchChange(pending.organization, false)} /> : null}
        {action === 'flagged_content' ? (
          <>
            {flaggedContentFieldset()}
            <ChangeSet title="Change" items={flaggedContentChange(pending.organization, humanReview)} />
          </>
        ) : null}
        {actionError ? <Alert variant="error">{actionError}</Alert> : null}
      </Dialog>
    </PageLayout>
  )
}
